'use client';
/* eslint-disable max-lines-per-function */

/**
 * Family / Guardian Access — Switch Family button (Unit U4).
 *
 * REQUIREMENTS SUMMARY (U4 — Active family context + Switch Family):
 * - R11/R13 Renders ONLY when the user has MORE THAN ONE active family in the
 *   selected organization. Single-family users (and everyone while the flag is
 *   off) see nothing — no switcher noise.
 * - Clicking opens SwitchFamilyDialog; choosing a family routes the choice through
 *   the server-validated context (useFamilyContext.switchFamily) and closes.
 * - The label shows the active family so the parent always knows which household
 *   they are viewing.
 *
 * FLAG SAFETY: useFamilyContext returns the inert value while the flag is off
 * (enabled:false, families:[]), so this component renders null and the host
 * (AppHeader / account menu) is behavior-neutral.
 *
 * VARIANTS: `header` (compact pill for the desktop top bar) and `menu` (full-width
 * row for the mobile/account dropdown). Both share the same dialog + handler.
 *
 * DRY: reuses useFamilyContext (the canonical family state) + SwitchFamilyDialog.
 */

import React, { useState } from 'react';
import { useFamilyContext } from '@/hooks/family/useFamilyContext';
import { SwitchFamilyDialog } from './SwitchFamilyDialog';

export interface SwitchFamilyButtonProps {
  /** `header` = compact desktop pill; `menu` = full-width account-menu row. */
  variant?: 'header' | 'menu';
  className?: string;
  /** Called after a successful switch (e.g. to close a parent dropdown menu). */
  onSwitched?: () => void;
}

/**
 * Whether SwitchFamilyButton would render anything right now (R11/R13: flag on
 * AND more than one family). Exported so a host that hides its own chrome around
 * the switcher — e.g. the public catalog's ⋮ menu inside the app's webview,
 * where the switcher is the only remaining row — asks the same question the
 * button answers, instead of re-deriving it and drifting.
 */
export function useCanSwitchFamily(): boolean {
  const { enabled, families } = useFamilyContext();
  return enabled && families.length > 1;
}

export function SwitchFamilyButton({
  variant = 'header',
  className = '',
  onSwitched,
}: SwitchFamilyButtonProps) {
  const { families, activeFamilyAccountId, activeFamily, switchFamily, isLoading } =
    useFamilyContext();
  const canSwitch = useCanSwitchFamily();
  const [open, setOpen] = useState(false);

  // Render ONLY for multi-family users while the flag is on (R11/R13).
  if (!canSwitch) {
    return null;
  }

  const handleSelect = async (familyAccountId: string) => {
    if (familyAccountId !== activeFamilyAccountId) {
      await switchFamily(familyAccountId);
      onSwitched?.();
    }
    setOpen(false);
  };

  const activeLabel = activeFamily?.displayName ?? 'Choose family';

  if (variant === 'menu') {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          // The `menu` variant exists to sit inside a role="menu" container, and
          // ARIA requires such a container's children to be menuitems.
          role="menuitem"
          className={`flex w-full items-center justify-between gap-3 px-4 py-3 min-h-[44px] text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.03] ${className}`}
        >
          <span className="flex items-center gap-2">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M16 3l5 5-5 5M21 8H9M8 21l-5-5 5-5M3 16h12"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="flex flex-col">
              <span className="font-medium">Switch family</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[180px]">
                Viewing {activeLabel}
              </span>
            </span>
          </span>
        </button>
        <SwitchFamilyDialog
          isOpen={open}
          families={families}
          activeFamilyAccountId={activeFamilyAccountId}
          onSelect={handleSelect}
          onClose={() => setOpen(false)}
          busy={isLoading}
        />
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className={`inline-flex items-center gap-2 min-h-[44px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/[0.03] ${className}`}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M16 3l5 5-5 5M21 8H9M8 21l-5-5 5-5M3 16h12"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="truncate max-w-[140px]">{activeLabel}</span>
      </button>
      <SwitchFamilyDialog
        isOpen={open}
        families={families}
        activeFamilyAccountId={activeFamilyAccountId}
        onSelect={handleSelect}
        onClose={() => setOpen(false)}
        busy={isLoading}
      />
    </>
  );
}

export default SwitchFamilyButton;
